import React, { useState } from 'react';
import type { MembershipChecklist } from '../domain/audience';
import type { PersonRef } from '../domain/people';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { fullName } from '../utils/people';
import { ActionError, ErrorState } from './PageStates';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

export type EndConnectionMode = 'unfollow' | 'remove';

/**
 * Ending a connection, both ways, in one step (015 FR-002–FR-006). Unfollow
 * offers to leave their private collections too, unchecked; remove offers to
 * take them out of mine, checked. The checklist is the server's; confirming
 * sends what's checked.
 */
export default function EndConnectionDialog({
  mode,
  person,
  open,
  onOpenChange,
  onDone,
}: {
  mode: EndConnectionMode;
  person: PersonRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-3xl border-none bg-background">
        {open && (
          <EndConnectionBody
            mode={mode}
            person={person}
            onDone={() => {
              onOpenChange(false);
              onDone();
            }}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EndConnectionBody({
  mode,
  person,
  onDone,
}: {
  mode: EndConnectionMode;
  person: PersonRef;
  onDone: () => void;
}) {
  const { audience } = useRepositories();
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const preview = useLoad<MembershipChecklist>(async () => {
    const list =
      mode === 'unfollow'
        ? await audience.unfollowPreview(person.id)
        : await audience.removeFollowerPreview(person.id);
    setChecked(new Set(list.collections.filter((c) => c.checked).map((c) => c.collection.id)));
    return list;
  }, `${mode}-${person.id}`);

  const first = person.firstName;
  const verb = mode === 'unfollow' ? 'Unfollow' : 'Remove';

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const ids = [...(checked ?? [])];
    try {
      if (mode === 'unfollow') await audience.unfollow(person.id, ids);
      else await audience.removeFollower(person.id, ids);
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {verb} {fullName(person)}?
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-2">
            <p>
              You and {first} will no longer be connected. You'll stop seeing {first}'s open
              collections, and {first} will stop seeing yours.
            </p>
            <p>To see less from {first} and stay connected, mute their collections instead.</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      {preview.status === 'loading' ? (
        <div role="status" aria-label="Loading" className="space-y-2">
          <Skeleton className="h-12 rounded-2xl" />
        </div>
      ) : preview.status === 'error' ? (
        <ErrorState message="Couldn't load the collections." onRetry={preview.reload} />
      ) : (
        preview.data.collections.length > 0 && (
          <fieldset>
            <legend className="mb-3 text-sm font-semibold text-foreground">
              {mode === 'unfollow'
                ? `Also leave ${first}'s private collections:`
                : `Also remove ${first} from your private collections:`}
            </legend>
            <ul className="space-y-2">
              {preview.data.collections.map(({ collection }) => {
                const id = `end-${collection.id}`;
                return (
                  <li
                    key={collection.id}
                    className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3"
                  >
                    <Checkbox
                      id={id}
                      checked={checked?.has(collection.id) ?? false}
                      onCheckedChange={(on) => toggle(collection.id, on === true)}
                    />
                    <Label htmlFor={id} className="flex-1 cursor-pointer font-medium">
                      {collection.name}
                    </Label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        )
      )}

      {error !== null && <ActionError error={error} />}

      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
        <Button
          className="rounded-full"
          variant="destructive"
          disabled={busy || preview.status !== 'ready'}
          onClick={confirm}
        >
          {verb}
        </Button>
      </AlertDialogFooter>
    </>
  );
}
